import React from 'react';

const TermsOfService = () => {
  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem',
      lineHeight: '1.6',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>
        CoinHeatMap Terms of Service (ToS)
      </h1>
      
      <p>
        Welcome to CoinHeatMap. These Terms of Service (“Terms”) govern your access to and use of 
        the CoinHeatMap website, platform, and related services (collectively, the “Services”), including 
        the use of our native $HEAT token. By accessing or using our Services (including holding or 
        transacting in $HEAT), you agree to be bound by these Terms. If you do not agree, you must not 
        use the Services. These Terms are designed to comply with international laws and regulations 
        applicable to cryptocurrency platforms, to protect both you and CoinHeatMap from legal risks, 
        and to ensure a transparent, user-friendly experience.
      </p>

      <h2>1. Regulatory Compliance and Global Legal Alignment</h2>
      <p>
        CoinHeatMap is committed to full compliance with global cryptocurrency regulations. We operate 
        in accordance with major regulatory frameworks and require that you (the user) also comply with 
        all applicable laws. Key compliance areas include:
      </p>

      <h3>United States</h3>
      <p>
        We adhere to guidance from U.S. regulators:
      </p>
      <ul>
        <li>
          <strong>Securities and Exchange Commission (SEC):</strong> We follow SEC guidelines to 
          determine that $HEAT is not a security. (Under the Howey test applied by the SEC, a digital 
          asset is a security if it involves an investment of money in a common enterprise with an 
          expectation of profits from the efforts of others [mintz.com]. $HEAT is not offered as an 
          investment and provides utility in our platform, as detailed below.) We do not offer or 
          sell securities, and any token offerings are conducted in compliance with SEC regulations.
        </li>
        <li>
          <strong>Commodity Futures Trading Commission (CFTC):</strong> We do not offer futures, swaps, 
          or derivatives on cryptocurrencies without proper CFTC compliance. (If we ever provide such 
          services, we will ensure registration or exemptions as required by the CFTC.)
        </li>
        <li>
          <strong>Financial Crimes Enforcement Network (FinCEN):</strong> CoinHeatMap complies with U.S. 
          anti-money laundering (AML) laws. If our activities qualify as money services business (e.g., 
          exchanging or transmitting crypto), we will register with FinCEN and implement Know-Your-Customer 
          (KYC) procedures [investopedia.com]. We report any suspicious activities as required under 
          the Bank Secrecy Act.
        </li>
        <li>
          <strong>Internal Revenue Service (IRS):</strong> For U.S. taxpayers, cryptocurrency is treated 
          as property for tax purposes [investopedia.com]. CoinHeatMap may provide you with transaction 
          history or forms for tax reporting, but you are responsible for reporting and paying any taxes 
          due on cryptocurrency transactions. We comply with any IRS reporting requirements (for example, 
          Form 1099 disclosures for certain transactions) in accordance with U.S. law.
        </li>
        <li>
          <strong>FATF Compliance:</strong> We adhere to the Financial Action Task Force recommendations 
          on AML/CFT (countering the financing of terrorism). In particular, we comply with the “Travel 
          Rule” by collecting and transmitting required originator and beneficiary information for 
          cryptocurrency transfers, in line with global standards [kychub.com].
        </li>
      </ul>

      <h3>European Union</h3>
      <ul>
        <li>
          <strong>MiCA (Markets in Crypto-Assets Regulation):</strong> CoinHeatMap follows the EU’s 
          MiCA framework for crypto assets. Under MiCA, $HEAT qualifies as a utility token (a type of 
          crypto-asset intended to provide digital access to an application, not a security or e-money) 
          [legalnodes.com]. We do not offer any asset-referenced tokens or e-money tokens without 
          proper authorization. We will comply with applicable MiCA requirements as they come into 
          effect (e.g., potential registration or disclosure obligations for utility token issuers).
        </li>
        <li>
          <strong>AMLD5/AMLD6 (Anti-Money Laundering Directives):</strong> We meet the EU’s AML 
          requirements. The 5th AML Directive expanded AML laws to cover cryptocurrency exchanges 
          and custodial wallet providers, requiring customer due diligence (KYC) and banning anonymous 
          use [watsonlaw.nl]. Accordingly, CoinHeatMap may require identity verification before you 
          can use certain Services (for example, high-value transactions or fiat conversions) to 
          prevent money laundering and terrorist financing. We also adhere to the 6th AML Directive 
          and any other applicable local AML laws in EU member states.
        </li>
        <li>
          <strong>GDPR (General Data Protection Regulation):</strong> We handle personal data of EU 
          users in compliance with GDPR. (The GDPR’s purpose is to protect individuals’ personal data 
          and ensure organizations process it lawfully and for legitimate purposes [techtarget.com].) 
          Please see the Privacy and Data Protection section below for more details, but in summary we: 
          obtain consent or have another legal basis for processing personal data, use data only for 
          the purposes provided, minimize data collection to what is necessary, protect data with 
          appropriate security, and honor user rights under GDPR.
        </li>
      </ul>

      <h3>United Kingdom</h3>
      <ul>
        <li>
          <strong>FCA Cryptoasset Guidance:</strong> The UK FCA generally classifies tokens into 
          security tokens, e-money tokens, and unregulated tokens (which include exchange tokens like 
          Bitcoin and utility tokens) [kychub.com]. $HEAT is intended to be a utility token (similar 
          to an exchange token in FCA terms) and not a regulated security or e-money. Thus, 
          CoinHeatMap’s token activities are designed to remain outside the FCA’s securities 
          regulatory perimeter [kychub.com]. However, we still comply with the FCA’s anti-money 
          laundering (AML) requirements. Cryptoasset businesses in the UK must register with the FCA 
          for AML supervision under the Money Laundering Regulations, as amended to implement FATF 
          standards [kychub.com]. If CoinHeatMap engages in in-scope activities in the UK (such as 
          providing exchange services or custodial services to UK customers), we will ensure FCA 
          registration and ongoing compliance. We also follow the UK’s financial promotions rules 
          when applicable (for example, we will include proper risk warnings on any marketing to UK 
          users, in line with FCA rules).
        </li>
        <li>
          <strong>UK AML and CTF:</strong> We implement controls required by UK law to prevent money 
          laundering and counter terrorist financing (CTF), including customer verification, transaction 
          monitoring, and record-keeping. “Know Your Customer” checks are required for users in the UK 
          as per the Money Laundering Regulations 2017 and subsequent updates. (Operating in the UK 
          without required registration or failing to comply with AML obligations is a criminal 
          offence [fintechfutures.com], so we take these requirements seriously.)
        </li>
      </ul>

      <h3>Other Jurisdictions</h3>
      <p>
        We also strive to comply with regulations in other key jurisdictions, such as Singapore, Japan, 
        Australia, and Canada. We have structured $HEAT to be a utility token under the relevant laws 
        and aim to fulfill AML/KYC and other obligations where applicable. If providing the Services 
        in a certain region would violate local laws or expose us to legal risk, we may restrict or 
        refuse access to users in that region.
      </p>

      <h2>2. Nature of the $HEAT Token – Utility Token, Not a Security</h2>
      <p>
        <strong>$HEAT is a Utility Token:</strong> CoinHeatMap’s native token, $HEAT, is provided 
        solely for use within the CoinHeatMap platform as a functional utility. Its primary purpose 
        is to enable and reward access to certain features, benefits, or services on CoinHeatMap (for 
        example, to Use-to-Earn by gaining token rewards for platform interactions, or to unlock 
        advanced analytics tools). $HEAT is not designed or marketed as an investment, and holding 
        $HEAT is not intended to generate profits from the efforts of CoinHeatMap or anyone else.
      </p>
      <p>
        We have taken care to ensure $HEAT does not meet the definition of an investment contract 
        (security) under the U.S. Howey Test or equivalent tests in other jurisdictions. Notably, you 
        should not purchase or hold $HEAT with any expectation of profit derived from CoinHeatMap’s 
        efforts [mintz.com]. The value of $HEAT, if any, is derived from its utility on the platform, 
        not from entrepreneurial or managerial efforts intended to drive token price.
      </p>
      <p>
        By using $HEAT, you acknowledge and agree that the token is not a security or investment and 
        that CoinHeatMap is not obligated to take any actions to create token value. Any secondary 
        market trading of $HEAT (on third-party exchanges, etc.) is beyond CoinHeatMap’s control and 
        is not encouraged or facilitated by us. We may, at our discretion, list or integrate $HEAT 
        with certain platforms for user convenience, but such actions do not change the nature of 
        $HEAT as a platform utility token.
      </p>

      <h2>3. Use-to-Earn, Staking and Rewards Program Terms</h2>
      <p>
        CoinHeatMap may offer a Use-to-Earn program where users earn $HEAT tokens by using the platform. 
        We may also offer staking or holding programs, where users can lock up $HEAT for a period to 
        receive certain benefits or additional token rewards.
      </p>
      <ul>
        <li>
          <strong>No Guaranteed Returns:</strong> While you may earn $HEAT as a reward for using the 
          platform or staking, CoinHeatMap does not guarantee any rate of return or profit. Rewards 
          are subject to change at any time.
        </li>
        <li>
          <strong>Active Participation vs. Passive Income:</strong> The Use-to-Earn model rewards active 
          participation. Staking or holding $HEAT is optional and offered for your convenience, not as 
          a recommendation to invest.
        </li>
        <li>
          <strong>Legal Status of Staking:</strong> Regulatory uncertainties exist around staking and 
          rewards programs. We consider our token rewards a loyalty and engagement program, not a 
          securities offering. Laws vary by region, and some aspects of staking or rewards could be 
          deemed unlawful in certain jurisdictions [atomicwallet.io]. We may restrict access for users 
          in certain jurisdictions to remain compliant.
        </li>
        <li>
          <strong>Program Terms and Changes:</strong> Additional rules may apply to specific reward 
          programs, and we reserve the right to modify or terminate these programs at any time.
        </li>
        <li>
          <strong>No Advice or Endorsement:</strong> Participation is at your own risk. We do not advise 
          you on the benefits or risks of holding or staking $HEAT. 
        </li>
      </ul>

      <h2>4. User Eligibility and Responsibilities</h2>
      <ul>
        <li>
          <strong>Legal Age and Capacity:</strong> You must be at least the age of majority in your 
          jurisdiction (usually 18) or otherwise legally able to form a binding contract.
        </li>
        <li>
          <strong>No Prohibited Users:</strong> You cannot use CoinHeatMap if you are on any sanctions 
          list or in a country embargoed by the U.S., EU, UK, or other government.
        </li>
        <li>
          <strong>Compliance with Laws:</strong> You agree to comply with all applicable laws, 
          including those relating to cryptocurrency, consumer protection, tax, and AML. You must 
          not use CoinHeatMap for unlawful or fraudulent activity.
        </li>
        <li>
          <strong>Geographic Restrictions:</strong> Our Services may not be available in all 
          jurisdictions. Do not attempt to circumvent geo-blocking or residency-based restrictions.
        </li>
        <li>
          <strong>Accurate Information:</strong> You must provide and maintain true, current, and 
          complete information when creating an account.
        </li>
        <li>
          <strong>Account Security:</strong> You are responsible for protecting your account 
          credentials. Notify us immediately of any unauthorized use.
        </li>
        <li>
          <strong>Acceptable Use:</strong> You agree not to violate intellectual property rights, 
          upload malicious code, harass others, disrupt the platform, manipulate markets, or use 
          CoinHeatMap for illegal activities.
        </li>
        <li>
          <strong>User Wallets and External Accounts:</strong> You are responsible for external 
          wallets. CoinHeatMap is not liable for losses due to incorrect addresses or network issues.
        </li>
        <li>
          <strong>Taxes:</strong> You are responsible for all applicable taxes arising from your use 
          of the Services.
        </li>
        <li>
          <strong>Feedback:</strong> Any suggestions you provide can be used by CoinHeatMap without 
          restriction or compensation to you.
        </li>
      </ul>

      <h2>5. Disclaimers of Risk and No Financial Advice</h2>
      <p>
        <strong>General Risk Acknowledgment:</strong> Cryptocurrencies are experimental, volatile, 
        and risky. You assume all risks associated with crypto and blockchain technology. Prices can 
        fluctuate, and laws may change. 
      </p>
      <ul>
        <li>
          <strong>Market Volatility:</strong> Crypto prices can change dramatically over short periods. 
          You could lose all your money. The UK FCA warns crypto investors to be prepared to lose 
          everything [fintechfutures.com].
        </li>
        <li>
          <strong>Regulatory Uncertainty:</strong> Laws may change and affect the value or legality of 
          crypto. We cannot predict or control regulatory actions.
        </li>
        <li>
          <strong>Technology and Security Risks:</strong> Blockchain and software systems can fail or 
          be hacked. You are responsible for protecting your own wallets and private keys.
        </li>
      </ul>
      <p>
        <strong>No Investment Advice:</strong> CoinHeatMap is not an investment advisor. Any data, 
        research, or analytics we provide is for informational purposes only. Do your own research and 
        consult professional advisors. We offer no guarantees about the accuracy or completeness of 
        information on the platform.
      </p>
      <p>
        <strong>No Fiduciary Duty:</strong> We have no fiduciary duties to you. We do not guarantee the 
        usefulness, timeliness, or accuracy of content on the platform.
      </p>
      <p>
        <strong>Not an Exchange or Broker:</strong> We do not currently execute trades or hold your 
        funds for trading. Any references to external exchanges or trades are not endorsements.
      </p>
      <p>
        In summary, use CoinHeatMap as an informational and utility tool, not as a financial advisor. 
        Always understand the high risks in crypto.
      </p>

      <h2>6. Privacy and Data Protection</h2>
      <p>
        Your privacy is important to us. Our collection, use, and sharing of personal information 
        is governed by our Privacy Policy, which is incorporated into these Terms by reference. 
        Highlights include:
      </p>
      <ul>
        <li>
          <strong>GDPR Compliance:</strong> For EU and UK users, we comply with GDPR/UK GDPR, including 
          data subject rights (access, rectification, deletion, etc.) [techtarget.com].
        </li>
        <li>
          <strong>CCPA Compliance:</strong> For California residents, you have certain rights (know, 
          delete, opt-out of sale, etc.). We do not sell personal data as defined under CCPA [cookiebot.com].
        </li>
        <li>
          <strong>Other Privacy Laws:</strong> We also aim to comply with other relevant data 
          protection laws around the world.
        </li>
        <li>
          <strong>Data We Collect:</strong> We may collect account info, usage data, device info, 
          wallet addresses, and KYC data where required by law.
        </li>
        <li>
          <strong>How We Use Data:</strong> We use it to provide and improve the Services, for 
          security, and to comply with legal obligations.
        </li>
        <li>
          <strong>How We Share Data:</strong> We do not share personal info with third parties except 
          for service providers under contract, legal compliance, or if you give us permission.
        </li>
        <li>
          <strong>Security:</strong> We use reasonable measures to protect data but cannot guarantee 
          absolute security.
        </li>
      </ul>
      <p>
        Please refer to our Privacy Policy for full details. By using CoinHeatMap, you consent to our 
        data practices as described in these Terms and the Privacy Policy.
      </p>

      <h2>7. Dispute Resolution and Governing Law</h2>
      <ul>
        <li>
          <strong>Governing Law:</strong> These Terms are governed by the laws of the State of Delaware, 
          USA, and applicable U.S. federal law.
        </li>
        <li>
          <strong>Arbitration Agreement:</strong> Except for small claims or intellectual property 
          matters, disputes must be resolved by binding arbitration under the American Arbitration 
          Association (AAA) rules. The arbitration is individual-only; no class actions or jury trials.
        </li>
        <li>
          <strong>30-Day Opt-Out Right:</strong> You can opt out of arbitration within 30 days of first 
          accepting these Terms by sending written notice to us.
        </li>
        <li>
          <strong>Time Limit to Bring Claims:</strong> Any claim must be brought within one year of 
          the event giving rise to it, or it is barred.
        </li>
        <li>
          <strong>Court Jurisdiction:</strong> If a dispute is found not subject to arbitration, it 
          must be litigated in Delaware. 
        </li>
      </ul>

      <h2>8. Limitation of Liability</h2>
      <p>
        CoinHeatMap provides the Services “as is.” To the fullest extent permitted by law, we disclaim 
        all warranties, and our liability is limited. We are not liable for indirect damages, lost 
        profits, or losses from market volatility or regulatory changes.
      </p>
      <p>
        <strong>Cap on Liability:</strong> Our total liability to you for claims related to the Services 
        will not exceed the greater of the fees you paid us in the 12 months preceding the claim or 
        $100 USD.
      </p>
      <p>
        We are not liable for events outside our reasonable control, third-party services, or 
        unauthorized access due to your security failures. 
      </p>

      <h2>9. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless CoinHeatMap and its affiliates from claims arising out 
        of your breach of these Terms, your use of the Services, your violation of law, or your 
        disputes with third parties. This obligation survives even after you stop using CoinHeatMap.
      </p>

      <h2>10. Intellectual Property Rights</h2>
      <p>
        All content on CoinHeatMap (excluding user-generated content and open blockchain data) is our 
        property or licensed to us. You receive a limited license to use the platform for personal, 
        non-commercial purposes. You retain ownership of content you submit, but grant us a license to 
        use it for the operation of the Services.
      </p>

      <h2>11. Termination</h2>
      <p>
        You may stop using CoinHeatMap at any time. We may suspend or terminate your access for 
        violation of these Terms, suspected illegal activity, or regulatory compliance reasons. 
        Certain sections of these Terms survive termination.
      </p>

      <h2>12. Miscellaneous</h2>
      <ul>
        <li>
          <strong>Entire Agreement:</strong> These Terms (plus the Privacy Policy) constitute the entire 
          agreement and supersede prior understandings.
        </li>
        <li>
          <strong>Clarification of Utility:</strong> By holding or using $HEAT, you acknowledge that it is provided solely as a utility token for accessing platform features and rewards. $HEAT does not confer any rights to ownership, dividends, profit-sharing, or any interest in CoinHeatMap’s revenues. Any value appreciation of $HEAT in secondary markets is solely the result of market dynamics and not an indication of guaranteed returns from our efforts.
        </li>
        <li>
          <strong>Amendments:</strong> We may update these Terms. Continuing to use the Services means 
          you accept the changes.
        </li>
        <li>
          <strong>Severability:</strong> Invalid provisions are enforced to the maximum extent 
          permissible, and the remainder stays in effect.
        </li>
        <li>
          <strong>No Waiver:</strong> Failure to exercise any right is not a waiver of future rights.
        </li>
        <li>
          <strong>Assignment:</strong> You cannot assign these Terms without our consent. We can 
          assign them in connection with a merger or sale of assets.
        </li>
        <li>
          <strong>No Third-Party Beneficiaries:</strong> Only you and CoinHeatMap have rights under 
          these Terms.
        </li>
        <li>
          <strong>Relationship:</strong> We are independent contractors, not partners or agents.
        </li>
        <li>
          <strong>Contact Information:</strong> If you have questions, contact bd@coinheatmap.
        </li>
      </ul>

      <p>
        By using CoinHeatMap’s Services, you acknowledge that you have read, understood, and agree to 
        all the above terms. We appreciate your trust and confidence in CoinHeatMap and look forward 
        to providing you with a valuable, compliant, and secure crypto platform.
      </p>
    </div>
  );
};

export default TermsOfService;
